import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { usePageTitle } from "@/hooks/use-page-title";
import { useLocation } from "wouter";
import { 
  Search, 
  Filter, 
  Users, 
  Building2, 
  Heart, 
  Home,
  Users2,
  Wallet,
  TrendingUp,
  Clock,
  ChevronRight,
  X,
  Globe,
  ExternalLink
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Progress } from "@/components/ui/progress";
import type { Wallet as WalletType } from "@shared/schema";

interface DiscoveryWallet extends WalletType {
  contributorCount: number;
}

function formatCurrency(cents: number | null | undefined): string {
  if (!cents) return "$0";
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(cents / 100);
}

function getWalletTypeIcon(walletType: string) {
  switch (walletType) {
    case "individual":
      return <Home className="h-4 w-4" />;
    case "family":
      return <Users className="h-4 w-4" />;
    case "community":
      return <Users2 className="h-4 w-4" />;
    case "organization":
      return <Building2 className="h-4 w-4" />;
    default:
      return <Wallet className="h-4 w-4" />;
  }
}

function getWalletTypeBadgeColor(walletType: string): string {
  switch (walletType) {
    case "individual":
      return "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200";
    case "family":
      return "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200";
    case "community":
      return "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200";
    case "organization":
      return "bg-amber-100 text-amber-800 dark:bg-amber-900 dark:text-amber-200";
    default:
      return "";
  }
}

function DiscoveryWalletCard({ wallet }: { wallet: DiscoveryWallet }) {
  const [, navigate] = useLocation();
  
  const displayName = wallet.organizationName || wallet.patientName || "Healthcare Wallet";
  const progress = wallet.goalAmount 
    ? Math.min(100, Math.round((wallet.totalContributions / wallet.goalAmount) * 100))
    : null;
  
  const handleContribute = () => {
    if (wallet.shareToken) {
      navigate(`/wallet/contribute/${wallet.shareToken}`);
    }
  };
  
  return (
    <Card 
      className="hover-elevate transition-all group"
      data-testid={`card-discover-wallet-${wallet.id}`}
    >
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2 flex-1 min-w-0">
            <Badge className={`shrink-0 ${getWalletTypeBadgeColor(wallet.walletType || "individual")}`}>
              {getWalletTypeIcon(wallet.walletType || "individual")}
              <span className="ml-1 capitalize">{wallet.walletType || "Individual"}</span>
            </Badge>
          </div>
          <Badge variant="outline" className="shrink-0">
            <Globe className="h-3 w-3 mr-1" />
            Public
          </Badge>
        </div>
        <CardTitle className="text-lg line-clamp-1 mt-2" data-testid={`text-wallet-name-${wallet.id}`}>
          {displayName}
        </CardTitle>
        {wallet.category && (
          <CardDescription className="text-sm">{wallet.category}</CardDescription>
        )}
      </CardHeader>
      
      <CardContent className="pb-4 space-y-4">
        {wallet.description && (
          <p className="text-sm text-muted-foreground line-clamp-2">
            {wallet.description}
          </p>
        )}
        
        <div className="space-y-2">
          <div className="flex items-center justify-between text-sm">
            <span className="text-muted-foreground">Raised</span>
            <span className="font-semibold text-lg">
              {formatCurrency(wallet.totalContributions)}
              {wallet.goalAmount && (
                <span className="text-muted-foreground font-normal text-sm">
                  {" "}/ {formatCurrency(wallet.goalAmount)}
                </span>
              )}
            </span>
          </div>
          
          {progress !== null && (
            <Progress value={progress} className="h-2" />
          )}
        </div>
        
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-1">
            <Heart className="h-4 w-4" />
            <span>{wallet.contributorCount} contributor{wallet.contributorCount !== 1 ? 's' : ''}</span>
          </div>
          {wallet.createdAt && (
            <div className="flex items-center gap-1">
              <Clock className="h-4 w-4" />
              <span>
                {new Date(wallet.createdAt).toLocaleDateString("en-US", { 
                  month: "short", 
                  year: "numeric" 
                })}
              </span>
            </div>
          )}
        </div>
      </CardContent>
      
      <CardFooter className="pt-0">
        <Button 
          className="w-full group-hover:translate-x-0"
          onClick={handleContribute}
          data-testid={`button-contribute-${wallet.id}`}
        >
          <Heart className="h-4 w-4 mr-2" />
          Contribute
          <ChevronRight className="h-4 w-4 ml-auto transition-transform group-hover:translate-x-1" />
        </Button>
      </CardFooter>
    </Card>
  );
}

function LoadingSkeleton() {
  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
      {[1, 2, 3, 4, 5, 6].map((i) => (
        <Card key={i}>
          <CardHeader>
            <Skeleton className="h-6 w-20" />
            <Skeleton className="h-5 w-3/4 mt-2" />
            <Skeleton className="h-4 w-1/2 mt-1" />
          </CardHeader>
          <CardContent>
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-4/5 mt-2" />
            <Skeleton className="h-2 w-full mt-4" />
            <div className="flex justify-between mt-4">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-4 w-16" />
            </div>
          </CardContent>
          <CardFooter>
            <Skeleton className="h-10 w-full" />
          </CardFooter>
        </Card>
      ))}
    </div>
  );
}

function EmptyState({ hasFilters, onClearFilters }: { hasFilters: boolean; onClearFilters: () => void }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
      <div className="flex h-16 w-16 items-center justify-center rounded-full bg-muted mb-4">
        <Search className="h-8 w-8 text-muted-foreground" />
      </div>
      <h3 className="text-lg font-medium" data-testid="text-no-results">
        {hasFilters ? "No matching wallets found" : "No public wallets available"}
      </h3>
      <p className="mt-2 text-sm text-muted-foreground max-w-sm">
        {hasFilters 
          ? "Try adjusting your search or filters to find more wallets."
          : "Check back later for public healthcare community support campaigns."}
      </p>
      {hasFilters && (
        <Button variant="outline" className="mt-4" onClick={onClearFilters}>
          <X className="h-4 w-4 mr-2" />
          Clear Filters
        </Button>
      )}
    </div>
  );
}

const WALLET_CATEGORIES = [
  "Chronic Illness",
  "Emergency Care",
  "Mental Health",
  "Prescription Medications",
  "Surgery & Procedures",
  "Dental Care",
  "Vision Care",
  "Physical Therapy",
  "Prenatal & Maternal",
  "Pediatric Care",
  "Senior Care",
  "Disability Support",
  "Cancer Treatment",
  "Other",
];

export default function WalletDiscoverPage() {
  usePageTitle("Discover Health Wallets", "Discover health wallets across the Minneapolis-St. Paul community and support healthcare funding.");
  const [, navigate] = useLocation();
  const [search, setSearch] = useState("");
  const [walletType, setWalletType] = useState<string>("");
  const [category, setCategory] = useState<string>("");
  const [sortBy, setSortBy] = useState<"recent" | "funding" | "contributors">("recent");
  
  const { data: wallets, isLoading, error } = useQuery<DiscoveryWallet[]>({
    queryKey: ["/api/wallets/discover", { search, walletType, category, sortBy }],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (walletType) params.append("walletType", walletType);
      if (category) params.append("category", category);
      if (sortBy) params.append("sortBy", sortBy);
      
      const res = await fetch(`/api/wallets/discover?${params.toString()}`);
      if (!res.ok) throw new Error("Failed to load wallets");
      return res.json();
    },
  });
  
  const hasFilters = search !== "" || walletType !== "" || category !== "";
  
  const clearFilters = () => {
    setSearch("");
    setWalletType("");
    setCategory("");
    setSortBy("recent");
  };
  
  return (
    <div className="container max-w-7xl py-8">
      <div className="flex flex-col gap-8">
        <div className="flex flex-col gap-2">
          <h1 className="text-3xl font-bold flex items-center gap-3" data-testid="text-discover-title">
            <Globe className="h-8 w-8" />
            Discover Wallets
          </h1>
          <p className="text-muted-foreground text-lg">
            Support healthcare campaigns in your community
          </p>
        </div>
        
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col gap-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search wallets by name, description, or organization..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="input-search-wallets"
                />
              </div>
              
              <div className="flex flex-wrap gap-3">
                <Select value={walletType} onValueChange={setWalletType}>
                  <SelectTrigger className="w-[180px]" data-testid="select-wallet-type">
                    <Filter className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Wallet Type" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="individual">
                      <div className="flex items-center gap-2">
                        <Home className="h-4 w-4" />
                        Individual
                      </div>
                    </SelectItem>
                    <SelectItem value="family">
                      <div className="flex items-center gap-2">
                        <Users className="h-4 w-4" />
                        Family
                      </div>
                    </SelectItem>
                    <SelectItem value="community">
                      <div className="flex items-center gap-2">
                        <Users2 className="h-4 w-4" />
                        Community
                      </div>
                    </SelectItem>
                    <SelectItem value="organization">
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4" />
                        Organization
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={category} onValueChange={setCategory}>
                  <SelectTrigger className="w-[200px]" data-testid="select-category">
                    <SelectValue placeholder="Category" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Categories</SelectItem>
                    {WALLET_CATEGORIES.map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
                  <SelectTrigger className="w-[180px]" data-testid="select-sort">
                    <TrendingUp className="h-4 w-4 mr-2" />
                    <SelectValue placeholder="Sort By" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="recent">Most Recent</SelectItem>
                    <SelectItem value="funding">Most Funded</SelectItem>
                    <SelectItem value="contributors">Most Contributors</SelectItem>
                  </SelectContent>
                </Select>
                
                {hasFilters && (
                  <Button variant="ghost" onClick={clearFilters} className="gap-2">
                    <X className="h-4 w-4" />
                    Clear Filters
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>
        
        {isLoading ? (
          <LoadingSkeleton />
        ) : error ? (
          <Card className="border-destructive">
            <CardContent className="py-8 text-center">
              <p className="text-destructive">
                Failed to load wallets. Please try again later.
              </p>
            </CardContent>
          </Card>
        ) : wallets && wallets.length > 0 ? (
          <>
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground" data-testid="text-wallet-count">
                Showing {wallets.length} wallet{wallets.length !== 1 ? 's' : ''}
              </p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {wallets.map((wallet) => (
                <DiscoveryWalletCard key={wallet.id} wallet={wallet} />
              ))}
            </div>
          </>
        ) : (
          <EmptyState hasFilters={hasFilters} onClearFilters={clearFilters} />
        )}
        
        <Card className="bg-primary/5 border-primary/20">
          <CardContent className="py-6">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-4">
                <div className="flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
                  <Wallet className="h-6 w-6 text-primary" />
                </div>
                <div>
                  <h3 className="font-semibold">Need help with healthcare costs?</h3>
                  <p className="text-sm text-muted-foreground">
                    Create your own HealthWallet and start receiving contributions
                  </p>
                </div>
              </div>
              <Button onClick={() => navigate("/wallet/create")} data-testid="button-create-own-wallet">
                Create Your Wallet
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
